import {
  ComponentType,
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DRAWERS } from '~components/shared/drawer/constants';

import { useSpecificDrawer } from '~hooks/use-specific-drawer';

const CoveredRisksReminderDrawer = lazy(() =>
  import(
    '~components/shared/drawer/drawers/covered-risks-reminder-drawer'
  ).then((m) => ({ default: m.CoveredRisksReminderDrawer })),
);

const PlannedActionsDrawer = lazy(() =>
  import(
    '~components/shared/drawer/drawers/planned-actions-drawer/planned-actions-drawer'
  ).then((m) => ({
    default: m.PlannedActionsDrawer,
  })),
);

const ReminderTemplateViewDrawer = lazy(() =>
  import(
    '~components/shared/drawer/drawers/reminder-template-view-drawer/reminder-template-view-drawer'
  ).then((m) => ({ default: m.ReminderTemplateViewDrawer })),
);

const RoutineCompletedTasksDrawer = lazy(() =>
  import(
    '~components/shared/drawer/drawers/routine-completed-tasks-drawer/routine-completed-tasks-drawer'
  ).then((m) => ({ default: m.RoutineCompletedTasksDrawer })),
);

const TenantsWithRemindersDrawer = lazy(() =>
  import(
    '~components/shared/drawer/drawers/tenants-with-reminders-drawer'
  ).then((m) => ({ default: m.TenantsWithRemindersDrawer })),
);

const AttentionDrawer = lazy(() =>
  import('~components/shared/drawer/drawers/attention-drawer').then((m) => ({
    default: m.AttentionDrawer,
  })),
);

const ReminderActionViewDrawer = lazy(() =>
  import(
    '~components/shared/drawer/drawers/reminder-action-view-drawer/reminder-action-view-drawer'
  ).then((m) => ({
    default: m.ReminderActionViewDrawer,
  })),
);

const ReminderActionsDateListDrawer = lazy(() =>
  import(
    '~components/shared/drawer/drawers/reminder-actions-date-list-drawer/reminder-actions-date-list-drawer'
  ).then((m) => ({
    default: m.ReminderActionsDateListDrawer,
  })),
);

const ReminderPreviewDrawer = lazy(() =>
  import(
    '~components/shared/drawer/drawers/reminder-preview-drawer/reminder-preview-drawer'
  ).then((m) => ({
    default: m.ReminderPreviewDrawer,
  })),
);

const TenantConnectionsDrawer = lazy(() =>
  import(
    '~components/shared/drawer/drawers/tenant-connections-drawer/tenant-connections-drawer'
  ).then((m) => ({
    default: m.TenantConnectionsDrawer,
  })),
);

const NotificationsDrawer = lazy(() =>
  import(
    '~components/shared/drawer/drawers/notifications-drawer/notifications-drawer'
  ).then((m) => ({
    default: m.NotificationsDrawer,
  })),
);

export type DrawerProps<T = any> = {
  open: boolean;
  data?: T;
};

type MountedDrawer = {
  drawerId: string;
  open: boolean;
  data?: any;
};

const EXIT_MS = 300;

const DRAWERS_MAP: Record<string, ComponentType<DrawerProps>> = {
  [DRAWERS.PLANNED_ACTIONS_DRAWER]: PlannedActionsDrawer,
  [DRAWERS.TENANTS_WITH_REMINDERS_DRAWER]: TenantsWithRemindersDrawer,
  [DRAWERS.COVERED_RISKS_REMINDER_DRAWER]: CoveredRisksReminderDrawer,
  [DRAWERS.ROUTINE_COMPLETED_TASKS_DRAWER]: RoutineCompletedTasksDrawer,
  [DRAWERS.REMINDER_TEMPLATE_VIEW_DRAWER]: ReminderTemplateViewDrawer,
  [DRAWERS.ATTENTION_DRAWER]: AttentionDrawer,
  [DRAWERS.REMINDER_ACTION_DRAWER]: ReminderActionViewDrawer,
  [DRAWERS.REMINDER_ACTIONS_DATE_LIST_DRAWER]: ReminderActionsDateListDrawer,
  [DRAWERS.REMINDER_PREVIEW_DRAWER]: ReminderPreviewDrawer,
  [DRAWERS.TENANT_CONNECTIONS_DRAWER]: TenantConnectionsDrawer,
  [DRAWERS.NOTIFICATIONS_DRAWER]: NotificationsDrawer,
} as const;

export type DrawerKeys = keyof typeof DRAWERS_MAP;

export const DrawerManager = () => {
  const { activeDrawers } = useSpecificDrawer();

  // drawerId -> mounted state
  const [mounted, setMounted] = useState<Record<string, MountedDrawer>>({});

  // drawerId -> remove timeout id
  const timersRef = useRef<Record<string, number>>({});

  const activeMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const d of activeDrawers) map.set(d.drawerId, d.data);
    return map;
  }, [activeDrawers]);

  useEffect(() => {
    setMounted((prev) => {
      const next = { ...prev };

      // 1) ensure active drawers are mounted & open
      for (const [drawerId, data] of activeMap.entries()) {
        // cancel pending removal
        if (timersRef.current[drawerId]) {
          window.clearTimeout(timersRef.current[drawerId]);
          delete timersRef.current[drawerId];
        }

        next[drawerId] = { drawerId, open: true, data };
      }

      // 2) drawers that are mounted but no longer active -> start closing (open=false) + schedule unmount
      for (const drawerId of Object.keys(next)) {
        if (!activeMap.has(drawerId) && next[drawerId].open) {
          next[drawerId] = { ...next[drawerId], open: false };

          timersRef.current[drawerId] = window.setTimeout(() => {
            setMounted((curr) => {
              const copy = { ...curr };
              delete copy[drawerId];
              return copy;
            });
            delete timersRef.current[drawerId];
          }, EXIT_MS);
        }
      }

      return next;
    });
  }, [activeMap]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      for (const t of Object.values(timersRef.current)) window.clearTimeout(t);
      timersRef.current = {};
    };
  }, []);

  return (
    <Suspense fallback={null}>
      {Object.values(mounted).map(({ drawerId, open, data }) => {
        const DrawerComponent = DRAWERS_MAP[drawerId];
        if (!DrawerComponent) return null;

        return <DrawerComponent key={drawerId} open={open} data={data} />;
      })}
    </Suspense>
  );
};
